import { useEffect, useMemo, useState } from "react";
import { ExpandedState } from "@tanstack/react-table";
import { Entity } from "@common/types/entity";
import { useGroupedView } from "@design-system/ViewTable/grouping/useGroupedView";
import {
    GROUP_ROW_ID_PREFIX,
    GroupChildrenState,
    GroupDescriptor,
} from "@design-system/ViewTable/grouping/groupByRowTypes";
import {
    organizeReportingCatalog,
    ReportingGroupBy,
    ReportingSort,
} from "./reportingControls";
import { ReportingCatalogItem } from "./reportingCatalog";
import {
    getReportingReportGroupByColumns,
    reportingEntityGroupByColumns,
    renderReportingScopeIcon,
    ReportingGroupByColumn,
} from "./reportingGroupByConfig";

interface UseReportingOrganizationParams {
    catalog: readonly ReportingCatalogItem[];
    entityById: Map<number, Entity>;
    groupBy: ReportingGroupBy;
    sort: ReportingSort;
    search: string;
    parentName: string;
}

const settledState = (
    items: ReportingCatalogItem[],
): GroupChildrenState<ReportingCatalogItem> => ({
    items,
    hasMore: false,
    isLoadingFirstPage: false,
    isLoadingMore: false,
    isError: false,
    isSettled: true,
});

function matchesSearch(item: ReportingCatalogItem, search: string): boolean {
    const term = search.toLowerCase();
    return (
        item.label.toLowerCase().includes(term) ||
        item.description.toLowerCase().includes(term) ||
        item.scope.label.toLowerCase().includes(term)
    );
}

export function useReportingOrganization({
    catalog,
    entityById,
    groupBy,
    sort,
    search,
    parentName,
}: UseReportingOrganizationParams) {
    const [expanded, setExpanded] = useState<ExpandedState>({});

    const filteredCatalog = useMemo(
        () => catalog.filter((item) => !search || matchesSearch(item, search)),
        [catalog, search],
    );

    const organizedCatalog = useMemo(
        () =>
            organizeReportingCatalog({
                items: filteredCatalog,
                groupBy,
                sort,
            }),
        [filteredCatalog, groupBy, sort],
    );

    const { groups, childrenByKey } = useMemo(() => {
        const descriptors: GroupDescriptor[] = organizedCatalog.groups.map(
            (descriptor) => ({
                ...descriptor,
                icon:
                    groupBy === "entity"
                        ? renderReportingScopeIcon({
                              scope: organizedCatalog.childrenByKey[
                                  descriptor.key
                              ][0].scope,
                              entityById,
                              parentName,
                          })
                        : undefined,
            }),
        );
        const states: Record<
            string,
            GroupChildrenState<ReportingCatalogItem>
        > = {};
        for (const descriptor of descriptors) {
            states[descriptor.key] = settledState(
                organizedCatalog.childrenByKey[descriptor.key] ?? [],
            );
        }
        return { groups: descriptors, childrenByKey: states };
    }, [organizedCatalog, groupBy, entityById, parentName]);

    const firstGroupRowId = groups[0]
        ? `${GROUP_ROW_ID_PREFIX}${groups[0].key}`
        : undefined;
    useEffect(() => {
        setExpanded(firstGroupRowId ? { [firstGroupRowId]: true } : {});
    }, [groupBy, firstGroupRowId]);

    const groupByColumns = useMemo(
        () =>
            groupBy === "report"
                ? getReportingReportGroupByColumns({
                      entityById,
                      parentName,
                  })
                : reportingEntityGroupByColumns,
        [entityById, groupBy, parentName],
    );

    const groupedViewProps = useGroupedView<
        ReportingCatalogItem,
        ReportingGroupByColumn,
        ReportingSort
    >({
        groups,
        childrenByKey,
        columns: groupByColumns,
        getItemRowId: (item) => item.id,
        onExpandGroup: NOOP,
        onLoadMore: NOOP,
        onRetry: NOOP,
        expanded,
        onExpandedChange: setExpanded,
        selectableRows: "items",
    });

    return {
        expanded,
        groupedViewProps,
        organizedCatalog,
    };
}

const NOOP = () => undefined;
